import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
    Assessment,
    Download,
    TrendingUp,
    BarChart,
    PieChart,
    ShowChart,
    Refresh
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { XhrContext } from '../../contexts/XhrContext';
import { fetchTransactions } from '../../services/transactionService';
import { storageKeys } from '../../config/environment';

const ReportsPanel = ({ caissierData }) => {
    const { selectedOrganization, user } = useAuth();
    const { getRequest } = useContext(XhrContext);
    const [dateRange, setDateRange] = useState('week');
    const [chartType, setChartType] = useState('bar');
    const [loading, setLoading] = useState(false);
    const [dashboard, setDashboard] = useState({
        yearlyAmount: 0,
        lastYearAmount: 0,
        monthlyAmount: 0,
        lastMonthAmount: 0,
        dailyAmount: 0,
        lastDayAmount: 0
    });

    // Charger les statistiques du dashboard (logique du mobile)
    const loadDashboardStats = useCallback(async () => {
        if (!selectedOrganization?.id) return;
        
        try {
            setLoading(true);
            const solutionId = localStorage.getItem(storageKeys.solution);
            
            const dashboardResponse = await getRequest("/trs/transactions/dashboard", {
                params: {
                    own: selectedOrganization.id,
                    srv: 'all',
                    act: user?.id || 'all',
                    crc: 'all',
                    pvd: 'all',
                    sol: solutionId
                }
            });
            
            if (dashboardResponse?.data) {
                setDashboard(dashboardResponse.data);
            }
        } catch (error) {
            console.warn('Error loading dashboard stats:', error);
        } finally {
            setLoading(false);
        }
    }, [selectedOrganization, user, getRequest]);

    useEffect(() => {
        if (selectedOrganization?.id) {
            loadDashboardStats();
        }
    }, [selectedOrganization?.id, loadDashboardStats]);

    const calculateAugmentation = (currentValue, lastValue) => {
        lastValue = lastValue || 0;
        currentValue = currentValue || 0;
        if (lastValue === 0 && currentValue === 0) {
            return 0;
        } else if (lastValue === 0 && currentValue > 0) {
            return 100;
        } else {
            return ((currentValue - lastValue) / lastValue) * 100;
        }
    };

    const formatNumber = (num) => {
        num = num === null ? 0 : num;
        if (num >= 1_000_000_000) {
            return (num / 1_000_000_000).toFixed(2).replace(/\.0$/, '') + 'B';
        } else if (num >= 1_000_000) {
            return (num / 1_000_000).toFixed(2).replace(/\.0$/, '') + 'M';
        } else if (num >= 1_000) {
            return (num / 1_000).toFixed(2).replace(/\.0$/, '') + 'K';
        } else {
            return num.toFixed(2).toString();
        }
    };

    const reportData = {
        revenue: {
            total: dashboard.dailyAmount || 0,
            change: calculateAugmentation(dashboard.dailyAmount, dashboard.lastDayAmount),
            trend: dashboard.dailyAmount >= dashboard.lastDayAmount ? 'up' : 'down'
        },
        transactions: {
            total: 0, // À calculer depuis les transactions
            change: 0,
            trend: 'up'
        },
        monthly: {
            total: dashboard.monthlyAmount || 0,
            change: calculateAugmentation(dashboard.monthlyAmount, dashboard.lastMonthAmount),
            trend: dashboard.monthlyAmount >= dashboard.lastMonthAmount ? 'up' : 'down'
        },
        yearly: {
            total: dashboard.yearlyAmount || 0,
            change: calculateAugmentation(dashboard.yearlyAmount, dashboard.lastYearAmount),
            trend: dashboard.yearlyAmount >= dashboard.lastYearAmount ? 'up' : 'down'
        }
    };

    const dailyData = [
        { day: 'Lun', revenue: 450, transactions: 23 },
        { day: 'Mar', revenue: 520, transactions: 28 },
        { day: 'Mer', revenue: 480, transactions: 25 },
        { day: 'Jeu', revenue: 610, transactions: 32 },
        { day: 'Ven', revenue: 785, transactions: 39 },
        { day: 'Sam', revenue: 0, transactions: 0 },
        { day: 'Dim', revenue: 0, transactions: 0 }
    ];

    const paymentMethods = [
        { method: 'QR Code', count: 67, percentage: 45.6, color: 'from-blue-500 to-cyan-500' },
        { method: 'Mobile Money', count: 45, percentage: 30.6, color: 'from-green-500 to-emerald-500' },
        { method: 'Carte Bancaire', count: 23, percentage: 15.6, color: 'from-purple-500 to-purple-600' },
        { method: 'Virement', count: 12, percentage: 8.2, color: 'from-orange-500 to-amber-500' }
    ];

    const maxRevenue = Math.max(...dailyData.map(d => d.revenue));

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    };

    return (
        <div className="space-y-8">
            {/* En-tête avec Filtres */}
            <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                        <div className="p-3 bg-gradient-to-r from-indigo-500 to-blue-500 rounded-2xl">
                            <Assessment className="text-white text-2xl" />
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900">Rapports et Analytics</h2>
                            <p className="text-gray-600">Analyse détaillée de vos performances</p>
                        </div>
                    </div>

                    <div className="flex items-center space-x-4">
                        {/* Bouton de rafraîchissement */}
                        <button
                            onClick={loadDashboardStats}
                            disabled={loading}
                            className="p-2.5 bg-gray-100 hover:bg-amber-50 rounded-xl transition-all border border-gray-200 hover:border-amber-200"
                        >
                            <Refresh className={`text-gray-700 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                        
                        {/* Sélecteur de Période */}
                        <select
                            value={dateRange}
                            onChange={(e) => setDateRange(e.target.value)}
                            className="bg-gray-100 border-0 rounded-2xl px-4 py-3 text-gray-700 focus:ring-2 focus:ring-amber-500 focus:outline-none"
                        >
                            <option value="today">Aujourd'hui</option>
                            <option value="week">Cette semaine</option>
                            <option value="month">Ce mois</option>
                            <option value="quarter">Ce trimestre</option>
                            <option value="year">Cette année</option>
                        </select>

                        {/* Sélecteur de Type de Graphique */}
                        <div className="flex bg-gray-100 rounded-2xl p-1">
                            <button
                                onClick={() => setChartType('bar')}
                                className={`p-2 rounded-xl transition-all ${
                                    chartType === 'bar'
                                        ? 'bg-white shadow-sm text-amber-600'
                                        : 'text-gray-400 hover:text-gray-600'
                                }`}
                            >
                                <BarChart />
                            </button>
                            <button
                                onClick={() => setChartType('line')}
                                className={`p-2 rounded-xl transition-all ${
                                    chartType === 'line'
                                        ? 'bg-white shadow-sm text-amber-600'
                                        : 'text-gray-400 hover:text-gray-600'
                                }`}
                            >
                                <ShowChart />
                            </button>
                            <button
                                onClick={() => setChartType('pie')}
                                className={`p-2 rounded-xl transition-all ${
                                    chartType === 'pie'
                                        ? 'bg-white shadow-sm text-amber-600'
                                        : 'text-gray-400 hover:text-gray-600'
                                }`}
                            >
                                <PieChart />
                            </button>
                        </div>

                        <button className="flex items-center space-x-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white px-6 py-3 rounded-2xl font-semibold hover:from-amber-600 hover:to-amber-700 transition-all duration-300 shadow-lg hover:shadow-xl">
                            <Download />
                            <span>Exporter</span>
                        </button>
                    </div>
                </div>

                {/* Statistiques Principales */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200">
                        <div className="flex items-center justify-between mb-3">
                            <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center">
                                <TrendingUp className="text-white text-xl" />
                            </div>
                            <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-semibold ${
                                reportData.revenue.trend === 'up'
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-red-100 text-red-700'
                            }`}>
                                <TrendingUp className="text-xs" />
                                <span>{reportData.revenue.change >= 0 ? '+' : ''}{reportData.revenue.change.toFixed(0)}%</span>
                            </div>
                        </div>
                        <div className="space-y-1">
                            <div className="text-2xl font-bold text-gray-900">
                                {formatNumber(dashboard.dailyAmount || 0)} FC
                            </div>
                            <div className="text-sm font-semibold text-gray-700">Aujourd'hui</div>
                            <div className="text-xs text-gray-500">Hier: {formatNumber(dashboard.lastDayAmount || 0)} FC</div>
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 border border-blue-200">
                        <div className="flex items-center justify-between mb-3">
                            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center">
                                <TrendingUp className="text-white text-xl" />
                            </div>
                            <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-semibold ${
                                reportData.monthly.trend === 'up'
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-red-100 text-red-700'
                            }`}>
                                <TrendingUp className="text-xs" />
                                <span>{reportData.monthly.change >= 0 ? '+' : ''}{reportData.monthly.change.toFixed(0)}%</span>
                            </div>
                        </div>
                        <div className="space-y-1">
                            <div className="text-2xl font-bold text-gray-900">
                                {formatNumber(dashboard.monthlyAmount || 0)} FC
                            </div>
                            <div className="text-sm font-semibold text-gray-700">Ce mois</div>
                            <div className="text-xs text-gray-500">Mois dernier: {formatNumber(dashboard.lastMonthAmount || 0)} FC</div>
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-6 border border-purple-200">
                        <div className="flex items-center justify-between mb-3">
                            <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center">
                                <TrendingUp className="text-white text-xl" />
                            </div>
                            <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-semibold ${
                                reportData.yearly.trend === 'up'
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-red-100 text-red-700'
                            }`}>
                                <TrendingUp className="text-xs" />
                                <span>{reportData.yearly.change >= 0 ? '+' : ''}{reportData.yearly.change.toFixed(0)}%</span>
                            </div>
                        </div>
                        <div className="space-y-1">
                            <div className="text-2xl font-bold text-gray-900">
                                {formatNumber(dashboard.yearlyAmount || 0)} FC
                            </div>
                            <div className="text-sm font-semibold text-gray-700">Cette année</div>
                            <div className="text-xs text-gray-500">Année dernière: {formatNumber(dashboard.lastYearAmount || 0)} FC</div>
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-2xl p-6 border border-amber-200">
                        <div className="flex items-center justify-between mb-3">
                            <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-amber-600 rounded-2xl flex items-center justify-center">
                                <Assessment className="text-white text-xl" />
                            </div>
                        </div>
                        <div className="space-y-1">
                            <div className="text-2xl font-bold text-gray-900">
                                Rapports
                            </div>
                            <div className="text-sm font-semibold text-gray-700">Analytics</div>
                            <div className="text-xs text-gray-500">Vue d'ensemble</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Graphiques */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                {/* Graphique des Revenus */}
                <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-6">Revenus Quotidiens</h3>

                    <div className="flex items-end justify-between h-64 space-x-2 pb-8">
                        {dailyData.map((day, index) => (
                            <div key={index} className="flex-1 flex flex-col items-center group">
                                <div className="relative w-full flex justify-center">
                                    {/* Barre du revenu */}
                                    <div
                                        className="w-3/4 bg-gradient-to-t from-blue-500 to-cyan-500 rounded-t-lg transition-all duration-500 ease-out group-hover:w-full group-hover:from-blue-600 group-hover:to-cyan-600"
                                        style={{ height: `${(day.revenue / maxRevenue) * 100}%` }}
                                    >
                                        <div className="absolute inset-0 bg-white/20 rounded-t-lg transform skew-x-12"></div>
                                    </div>

                                    {/* Tooltip */}
                                    <div className="absolute -top-12 opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:-translate-y-2 z-10">
                                        <div className="bg-gray-900 text-white px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap">
                                            {formatCurrency(day.revenue)}
                                            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1 w-2 h-2 bg-gray-900 rotate-45"></div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-3 text-xs font-medium text-gray-600">
                                    {day.day}
                                </div>
                                <div className="mt-1 text-xs text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity">
                                    {day.transactions} trans.
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Méthodes de Paiement */}
                <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-6">Méthodes de Paiement</h3>

                    <div className="space-y-4">
                        {paymentMethods.map((method, index) => (
                            <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
                                <div className="flex items-center space-x-4">
                                    <div className={`w-12 h-12 bg-gradient-to-r ${method.color} rounded-2xl flex items-center justify-center`}>
                                        <BarChart className="text-white text-xl" />
                                    </div>
                                    <div>
                                        <div className="font-semibold text-gray-900">{method.method}</div>
                                        <div className="text-sm text-gray-600">{method.count} transactions</div>
                                    </div>
                                </div>

                                <div className="text-right">
                                    <div className="font-bold text-gray-900">{method.percentage}%</div>
                                    <div className="text-sm text-gray-600">Part du total</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Légende du Graphique Circulaire */}
                    <div className="mt-6 grid grid-cols-2 gap-3">
                        {paymentMethods.map((method, index) => (
                            <div key={index} className="flex items-center space-x-2">
                                <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${method.color}`}></div>
                                <span className="text-sm text-gray-600">{method.method}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Tableau des Transactions Récentes */}
            <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Transactions Récentes</h3>

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                        <tr className="border-b border-gray-200">
                            <th className="text-left py-4 px-4 text-sm font-semibold text-gray-600">ID</th>
                            <th className="text-left py-4 px-4 text-sm font-semibold text-gray-600">Client</th>
                            <th className="text-left py-4 px-4 text-sm font-semibold text-gray-600">Montant</th>
                            <th className="text-left py-4 px-4 text-sm font-semibold text-gray-600">Méthode</th>
                            <th className="text-left py-4 px-4 text-sm font-semibold text-gray-600">Statut</th>
                            <th className="text-left py-4 px-4 text-sm font-semibold text-gray-600">Date</th>
                        </tr>
                        </thead>
                        <tbody>
                        {[1, 2, 3, 4, 5].map((item) => (
                            <tr key={item} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                                <td className="py-4 px-4 text-sm font-medium text-gray-900">TX{1000 + item}</td>
                                <td className="py-4 px-4 text-sm text-gray-700">Client {item}</td>
                                <td className="py-4 px-4 text-sm font-semibold text-amber-600">{formatCurrency(50 + item * 25)}</td>
                                <td className="py-4 px-4 text-sm text-gray-700">
                                    {['QR Code', 'Mobile Money', 'Carte'][item % 3]}
                                </td>
                                <td className="py-4 px-4">
                                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                                            Réussi
                                        </span>
                                </td>
                                <td className="py-4 px-4 text-sm text-gray-600">
                                    {new Date(Date.now() - item * 3600000).toLocaleDateString('fr-FR')}
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ReportsPanel;